import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { FileText, Plus, Edit, Trash2, Loader2, Save, Copy, Eye, Sparkles, Shield } from 'lucide-react';
import { templateAPI } from '../../api';
import { toast } from 'sonner';
import RichTextEditor from '../RichTextEditor';

const TEMPLATE_TYPES = [
  { value: 'notice', label: 'Notice' },
  { value: 'agenda', label: 'Agenda' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'resolution', label: 'Resolution' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'other', label: 'Other' },
];

const MEETING_TYPES = [
  { value: 'board', label: 'Board' },
  { value: 'committee', label: 'Committee' },
  { value: 'asm', label: 'ASM' },
  { value: 'ssm', label: 'SSM' },
];

const COMMON_PLACEHOLDERS = {
  'organization.name': 'Organization Name',
  'organization.address': 'Principal Office Address',
  'meeting.title': 'Meeting Title',
  'meeting.date': 'Meeting Date',
  'meeting.time': 'Meeting Time',
  'meeting.venue': 'Meeting Venue',
  'meeting.type': 'Meeting Type',
  'meeting.body_name': 'Body Name',
  'secretary.name': 'Corporate Secretary Name',
  'chairman.name': 'Chairman Name',
  'quorum.value': 'Quorum Value',
  'resolution.number': 'Resolution Number',
  'resolution.text': 'Resolution Text',
  'resolution.title': 'Resolution Title',
  'certificate.number': 'Certificate Number',
  'current_date': 'Current Date',
};

const TemplatesTab = ({ templates, user, fetchData }) => {
  const [tmplFilter, setTmplFilter] = useState('all');
  const [tmplDialogOpen, setTmplDialogOpen] = useState(false);
  const [editingTmpl, setEditingTmpl] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = tmplFilter === 'all' ? templates : templates.filter(t => t.template_type === tmplFilter);
  const tenantTmpls = filtered.filter(t => t.scope === 'TENANT_CUSTOM');
  const defaultTmpls = filtered.filter(t => t.scope === 'LANDLORD_DEFAULT');

  const typeGroups = {};
  TEMPLATE_TYPES.forEach(tt => {
    const tenantForType = tenantTmpls.filter(t => t.template_type === tt.value);
    const defaultForType = defaultTmpls.filter(t => t.template_type === tt.value);
    if (tenantForType.length > 0 || defaultForType.length > 0) {
      typeGroups[tt.value] = { label: tt.label, tenant: tenantForType, defaults: defaultForType };
    }
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingTmpl.id) {
        const { id, scope, created_at, created_by, updated_at, _preview, ...updates } = editingTmpl;
        await templateAPI.update(editingTmpl.id, updates);
      } else {
        await templateAPI.create({ ...editingTmpl, organization_id: user.organization_id });
      }
      toast.success('Template saved');
      setTmplDialogOpen(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h3 className="text-lg font-semibold">Document Templates</h3>
          <p className="text-sm text-[#5A6570]">Your custom templates override the platform defaults</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={tmplFilter} onValueChange={setTmplFilter}>
            <SelectTrigger className="w-[140px] h-9 border-[#E2E6EA]"><SelectValue placeholder="Filter" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TEMPLATE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="add-template-btn" onClick={() => {
            setEditingTmpl({ name: '', template_type: 'notice', applicable_meeting_types: [], body_html: '', placeholders: { ...COMMON_PLACEHOLDERS } });
            setTmplDialogOpen(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />New Template
          </Button>
        </div>
      </div>

      {Object.keys(typeGroups).length === 0 ? (
        <Card className="border-[#E2E6EA]">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
            <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No templates available</h3>
            <p className="text-[#5A6570] mb-4">Create custom templates or wait for platform defaults</p>
          </CardContent>
        </Card>
      ) : Object.entries(typeGroups).map(([type, group]) => (
        <div key={type} className="space-y-3">
          <h4 className="text-sm font-semibold text-[#1A1D21] uppercase tracking-wider border-b border-[#E2E6EA] pb-2">{group.label}</h4>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {group.tenant.map(tmpl => (
              <Card key={tmpl.id} className="border-[#00A3A3]/30 bg-[#00A3A3]/3">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-[#1A1D21] text-sm truncate">{tmpl.name}</h4>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Badge className="text-[10px] bg-[#00A3A3]/10 text-[#00A3A3] border-[#00A3A3]/20">Custom</Badge>
                        {tmpl.applicable_meeting_types?.map(mt => <Badge key={mt} variant="outline" className="text-[10px] uppercase">{mt}</Badge>)}
                      </div>
                    </div>
                    <div className="flex gap-0.5 ml-2">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-[#F58A26]" title="Preview" onClick={async () => {
                        try { const res = await templateAPI.preview(tmpl.id); setPreviewHtml(res.data.rendered_html); setPreviewName(res.data.template_name); setPreviewDialog(true); } catch { toast.error('Preview failed'); }
                      }}><Sparkles className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                        setEditingTmpl({ ...tmpl, placeholders: tmpl.placeholders && Object.keys(tmpl.placeholders).length > 0 ? tmpl.placeholders : { ...COMMON_PLACEHOLDERS } });
                        setTmplDialogOpen(true);
                      }}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => {
                        if (!window.confirm('Delete this custom template?')) return;
                        try { await templateAPI.delete(tmpl.id); toast.success('Template deleted'); fetchData(); } catch { toast.error('Failed'); }
                      }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {group.defaults.map(tmpl => (
              <Card key={tmpl.id} className="border-[#E2E6EA] bg-[#F8F9FA]">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[#5A6570] text-sm truncate">{tmpl.name}</h4>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Badge variant="outline" className="text-[10px] text-[#A8B1B9]">Platform Default</Badge>
                        {tmpl.source === 'SEC_RECOMMENDED' && <Badge className="text-[10px] bg-[#F58A26]/10 text-[#F58A26] border-[#F58A26]/20"><Shield className="h-2.5 w-2.5 mr-0.5" />SEC</Badge>}
                        {tmpl.applicable_meeting_types?.map(mt => <Badge key={mt} variant="outline" className="text-[10px] uppercase">{mt}</Badge>)}
                      </div>
                    </div>
                    <div className="flex gap-0.5 ml-2">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-[#F58A26]" title="Preview" onClick={async () => {
                        try { const res = await templateAPI.preview(tmpl.id); setPreviewHtml(res.data.rendered_html); setPreviewName(res.data.template_name); setPreviewDialog(true); } catch { toast.error('Preview failed'); }
                      }}><Sparkles className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Preview raw" onClick={() => {
                        setEditingTmpl({ ...tmpl, _preview: true, placeholders: tmpl.placeholders || {} });
                        setTmplDialogOpen(true);
                      }}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-[#00A3A3]" title="Clone & Customize" onClick={async () => {
                        try { await templateAPI.clone(tmpl.id); toast.success('Template cloned'); fetchData(); } catch { toast.error('Failed to clone'); }
                      }} data-testid={`clone-template-${tmpl.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {/* Template Edit/Create Dialog */}
      <Dialog open={tmplDialogOpen} onOpenChange={setTmplDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTmpl?._preview ? 'Preview Template' : editingTmpl?.id ? 'Edit Template' : 'New Custom Template'}</DialogTitle>
            <DialogDescription>{editingTmpl?._preview ? 'This is a platform default template. Clone it to customize.' : 'Customize this template for your organization'}</DialogDescription>
          </DialogHeader>
          {editingTmpl && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input value={editingTmpl.name} onChange={(e) => setEditingTmpl({ ...editingTmpl, name: e.target.value })} className="border-[#E2E6EA]" disabled={editingTmpl._preview} data-testid="tenant-template-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={editingTmpl.template_type} onValueChange={(v) => setEditingTmpl({ ...editingTmpl, template_type: v })} disabled={editingTmpl._preview}>
                    <SelectTrigger className="border-[#E2E6EA]"><SelectValue /></SelectTrigger>
                    <SelectContent>{TEMPLATE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Applicable Meeting Types</Label>
                <div className="flex flex-wrap gap-2">
                  {MEETING_TYPES.map(mt => {
                    const selected = editingTmpl.applicable_meeting_types?.includes(mt.value);
                    return (
                      <Button key={mt.value} type="button" variant={selected ? 'default' : 'outline'} size="sm"
                        className={`text-xs h-7 ${selected ? 'bg-[#00A3A3] hover:bg-[#008585]' : ''}`}
                        disabled={editingTmpl._preview}
                        onClick={() => {
                          const types = editingTmpl.applicable_meeting_types || [];
                          setEditingTmpl({ ...editingTmpl, applicable_meeting_types: selected ? types.filter(t => t !== mt.value) : [...types, mt.value] });
                        }}
                      >{mt.label}</Button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Template Body</Label>
                <RichTextEditor content={editingTmpl.body_html} onChange={(html) => setEditingTmpl({ ...editingTmpl, body_html: html })} placeholders={editingTmpl._preview ? {} : (editingTmpl.placeholders || COMMON_PLACEHOLDERS)} editable={!editingTmpl._preview} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTmplDialogOpen(false)}>{editingTmpl?._preview ? 'Close' : 'Cancel'}</Button>
            {editingTmpl?._preview ? (
              <Button className="bg-[#00A3A3] hover:bg-[#008585]" onClick={async () => {
                try { await templateAPI.clone(editingTmpl.id); toast.success('Template cloned'); setTmplDialogOpen(false); fetchData(); } catch { toast.error('Failed'); }
              }}><Copy className="h-4 w-4 mr-2" />Clone & Customize</Button>
            ) : (
              <Button onClick={handleSave} disabled={saving || !editingTmpl?.name} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-tenant-template-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#F58A26]" />Template Preview</DialogTitle>
            <DialogDescription>{previewName} — rendered with sample Philippine corporate data.</DialogDescription>
          </DialogHeader>
          <div className="border border-[#E2E6EA] rounded-lg bg-white p-6 mt-2">
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml }} data-testid="tenant-template-preview-content" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPreviewDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TemplatesTab;
